import React, { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { Button } from "../../../foundation/altzero_base/base/components/ui/button";
import { Input } from "../../../foundation/altzero_base/base/components/ui/input";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "../../../foundation/altzero_base/base/components/ui/card";
import { ResponsiveTable } from "../components/ResponsiveTable";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "../../../foundation/altzero_base/base/components/ui/dialog";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "../../../foundation/altzero_base/base/components/ui/alert-dialog";
import { Label } from "../../../foundation/altzero_base/base/components/ui/label";
import { useToast } from "../../../foundation/altzero_base/base/hooks/use-toast";
import {
  FileCheck,
  Plus,
  Search,
  Edit,
  Trash2,
  ArrowLeft,
  Building2,
  Calendar,
} from "lucide-react";
import ScopingAILayout from "../components/ScopingAILayout";
import {
  getLOAs,
  createLOA,
  updateLOA,
  deleteLOA,
  LOA,
} from "../services/loaService";
import { getClients, Client } from "../services/clientService";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../../../foundation/altzero_base/base/components/ui/select";

export default function LOAManagement() {
  const { toast } = useToast();
  const [loas, setLOAs] = useState<LOA[]>([]);
  const [filteredLOAs, setFilteredLOAs] = useState<LOA[]>([]);
  const [clients, setClients] = useState<Client[]>([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState("");
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [isDeleteDialogOpen, setIsDeleteDialogOpen] = useState(false);
  const [editingLOA, setEditingLOA] = useState<LOA | null>(null);
  const [deletingLOAId, setDeletingLOAId] = useState<string | null>(null);
  const [formData, setFormData] = useState({
    client_id: "",
    file_id: "",
    signed_on: "",
  });

  useEffect(() => {
    loadData();
  }, []);

  useEffect(() => {
    filterLOAs();
  }, [searchQuery, loas]);

  const loadData = async () => {
    setLoading(true);
    
    // Load clients
    const clientsResult = await getClients();
    if (clientsResult.success && clientsResult.data) {
      setClients(clientsResult.data);
    }

    // Load LOAs
    const loasResult = await getLOAs();
    if (loasResult.success && loasResult.data) {
      setLOAs(loasResult.data);
    } else {
      toast({
        title: "Error",
        description: loasResult.error || "Failed to load LOAs",
        variant: "destructive",
      });
    }
    
    setLoading(false);
  };

  const filterLOAs = () => {
    if (!searchQuery.trim()) {
      setFilteredLOAs(loas);
      return;
    }

    const query = searchQuery.toLowerCase();
    const filtered = loas.filter(
      (loa) =>
        loa.client?.name.toLowerCase().includes(query) ||
        loa.file_id?.toLowerCase().includes(query)
    );
    setFilteredLOAs(filtered);
  };

  const handleOpenDialog = (loa?: LOA) => {
    if (loa) {
      setEditingLOA(loa);
      setFormData({
        client_id: loa.client_id,
        file_id: loa.file_id || "",
        signed_on: loa.signed_on || "",
      });
    } else {
      setEditingLOA(null);
      setFormData({
        client_id: "",
        file_id: "",
        signed_on: "",
      });
    }
    setIsDialogOpen(true);
  };

  const handleCloseDialog = () => {
    setIsDialogOpen(false);
    setEditingLOA(null);
    setFormData({
      client_id: "",
      file_id: "",
      signed_on: "",
    });
  };

  const handleSubmit = async () => {
    if (!formData.client_id) {
      toast({
        title: "Validation Error",
        description: "Client is required",
        variant: "destructive",
      });
      return;
    }

    const result = editingLOA
      ? await updateLOA(editingLOA.id, formData)
      : await createLOA(formData);

    if (result.success) {
      toast({
        title: "Success",
        description: `LOA ${editingLOA ? "updated" : "created"} successfully`,
      });
      handleCloseDialog();
      loadData();
    } else {
      toast({
        title: "Error",
        description: result.error || `Failed to ${editingLOA ? "update" : "create"} LOA`,
        variant: "destructive",
      });
    }
  };

  const handleDeleteClick = (loaId: string) => {
    setDeletingLOAId(loaId);
    setIsDeleteDialogOpen(true);
  };

  const handleDeleteConfirm = async () => {
    if (!deletingLOAId) return;

    const result = await deleteLOA(deletingLOAId);

    if (result.success) {
      toast({
        title: "Success",
        description: "LOA deleted successfully",
      });
      loadData();
    } else {
      toast({
        title: "Error",
        description: result.error || "Failed to delete LOA",
        variant: "destructive",
      });
    }

    setIsDeleteDialogOpen(false);
    setDeletingLOAId(null);
  };

  const formatDate = (dateString: string | null) => {
    if (!dateString) return "—";
    return new Date(dateString).toLocaleDateString();
  };

  return (
    <ScopingAILayout>
      <div className="container mx-auto py-6 px-4 sm:px-6 lg:px-8 max-w-7xl">
        {/* Header */}
        <div className="mb-6 sm:mb-8">
          <Link
            to="/tender/resource-library"
            className="inline-flex items-center text-sm text-muted-foreground hover:text-primary mb-4 transition-colors"
          >
            <ArrowLeft className="mr-2 h-4 w-4" />
            Back to Resource Library
          </Link>
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
            <div>
              <h1 className="text-3xl sm:text-4xl font-bold text-gray-900 flex items-center gap-3">
                <FileCheck className="w-8 h-8 sm:w-10 sm:h-10 text-primary" />
                LOA Management
              </h1>
              <p className="text-base sm:text-lg text-muted-foreground mt-2">
                Manage Letters of Authority
              </p>
            </div>
            <Button
              onClick={() => handleOpenDialog()}
              size="lg"
              className="w-full sm:w-auto"
            >
              <Plus className="mr-2 h-5 w-5" />
              Add LOA
            </Button>
          </div>
        </div>

        {/* Search */}
        <Card className="mb-6">
          <CardContent className="pt-6">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-muted-foreground h-5 w-5" />
              <Input
                placeholder="Search LOAs by client name..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="pl-10 h-12 text-base"
              />
            </div>
          </CardContent>
        </Card>

        {/* LOAs Table */}
        <Card>
          <CardHeader>
            <CardTitle>All LOAs ({filteredLOAs.length})</CardTitle>
            <CardDescription>
              Letters of Authority in your system
            </CardDescription>
          </CardHeader>
          <CardContent>
            {loading ? (
              <div className="text-center py-8 text-muted-foreground">
                Loading LOAs...
              </div>
            ) : (
              <ResponsiveTable
                data={filteredLOAs}
                columns={[
                  {
                    key: "client",
                    label: "Client",
                    render: (loa) => (
                      <div className="flex items-center gap-2">
                        <Building2 className="h-4 w-4 text-muted-foreground" />
                        <span className="font-medium">{loa.client?.name || "—"}</span>
                      </div>
                    ),
                  },
                  {
                    key: "signed_on",
                    label: "Signed Date",
                    render: (loa) => (
                      <div className="flex items-center gap-2">
                        <Calendar className="h-4 w-4 text-muted-foreground" />
                        {formatDate(loa.signed_on)}
                      </div>
                    ),
                  },
                  {
                    key: "created_at",
                    label: "Created",
                    render: (loa) => formatDate(loa.created_at),
                  },
                ]}
                actions={(loa) => (
                  <>
                    <Button
                      variant="ghost"
                      size="icon"
                      onClick={() => handleOpenDialog(loa)}
                    >
                      <Edit className="h-4 w-4" />
                    </Button>
                    <Button
                      variant="ghost"
                      size="icon"
                      onClick={() => handleDeleteClick(loa.id)}
                    >
                      <Trash2 className="h-4 w-4 text-destructive" />
                    </Button>
                  </>
                )}
                mobileCardRender={(loa) => (
                  <div className="space-y-2">
                    <div className="flex items-center gap-2">
                      <Building2 className="h-4 w-4 text-muted-foreground" />
                      <span className="font-medium text-lg">{loa.client?.name || "—"}</span>
                    </div>
                    <div className="flex items-center gap-2 text-sm text-muted-foreground">
                      <Calendar className="h-4 w-4" />
                      Signed: {formatDate(loa.signed_on)}
                    </div>
                    <div className="text-sm text-muted-foreground">
                      Created: {formatDate(loa.created_at)}
                    </div>
                  </div>
                )}
                emptyMessage="No LOAs found. Add your first LOA to get started."
              />
            )}
          </CardContent>
        </Card>

        {/* Add/Edit Dialog */}
        <Dialog open={isDialogOpen} onOpenChange={setIsDialogOpen}>
          <DialogContent className="sm:max-w-[500px]">
            <DialogHeader>
              <DialogTitle>
                {editingLOA ? "Edit LOA" : "Add New LOA"}
              </DialogTitle>
              <DialogDescription>
                {editingLOA
                  ? "Update LOA information"
                  : "Add a new Letter of Authority"}
              </DialogDescription>
            </DialogHeader>
            <div className="space-y-4 py-4">
              <div className="space-y-2">
                <Label htmlFor="client_id">
                  Client <span className="text-red-500">*</span>
                </Label>
                <Select
                  value={formData.client_id}
                  onValueChange={(value) =>
                    setFormData({ ...formData, client_id: value })
                  }
                >
                  <SelectTrigger>
                    <SelectValue placeholder="Select a client" />
                  </SelectTrigger>
                  <SelectContent>
                    {clients.map((client) => (
                      <SelectItem key={client.id} value={client.id}>
                        {client.name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label htmlFor="signed_on">Signed Date</Label>
                <Input
                  id="signed_on"
                  type="date"
                  value={formData.signed_on}
                  onChange={(e) =>
                    setFormData({ ...formData, signed_on: e.target.value })
                  }
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="file_id">File ID</Label>
                <Input
                  id="file_id"
                  placeholder="File reference ID"
                  value={formData.file_id}
                  onChange={(e) =>
                    setFormData({ ...formData, file_id: e.target.value })
                  }
                />
              </div>
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={handleCloseDialog}>
                Cancel
              </Button>
              <Button onClick={handleSubmit}>
                {editingLOA ? "Update" : "Create"}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* Delete Confirmation Dialog */}
        <AlertDialog open={isDeleteDialogOpen} onOpenChange={setIsDeleteDialogOpen}>
          <AlertDialogContent>
            <AlertDialogHeader>
              <AlertDialogTitle>Are you sure?</AlertDialogTitle>
              <AlertDialogDescription>
                This action cannot be undone. This will permanently delete the
                LOA from your system.
              </AlertDialogDescription>
            </AlertDialogHeader>
            <AlertDialogFooter>
              <AlertDialogCancel>Cancel</AlertDialogCancel>
              <AlertDialogAction onClick={handleDeleteConfirm}>
                Delete
              </AlertDialogAction>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialog>
      </div>
    </ScopingAILayout>
  );
}

